// What each media category accepts, in one place. The upload dialog, drag
// and drop onto a category, paste and the camera all check files here before
// anything is sent: the server takes any type and size, and only answers a
// full storage quota.

const MB = 1024 * 1024;

const KINDS = {
  jpg: { mime: "image/jpeg", mimes: ["image/jpeg", "image/jpg"], exts: ["jpg", "jpeg"], label: "JPG", maxBytes: 25 * MB },
  png: { mime: "image/png", mimes: ["image/png"], exts: ["png"], label: "PNG", maxBytes: 25 * MB },
  pdf: { mime: "application/pdf", mimes: ["application/pdf"], exts: ["pdf"], label: "PDF", maxBytes: 25 * MB },
  dicom: { mime: "application/dicom", mimes: ["application/dicom"], exts: ["dcm", "dicom"], label: "DICOM", maxBytes: 50 * MB },
};

export const CATEGORY_KINDS = {
  IntraOralPhoto: ["jpg", "png"],
  ExtraOralPhoto: ["jpg", "png"],
  XRay: ["jpg", "png", "dicom"],
  OpdCard: ["jpg", "png", "pdf"],
  Document: ["pdf", "jpg", "png"],
  // Clinic forms are signed as PDFs: images are turned into one-page PDFs
  // before upload (see needsPdfWrap).
  Template: ["pdf", "jpg", "png"],
};

export const CATEGORY_LABELS = {
  IntraOralPhoto: "Intra-Oral",
  ExtraOralPhoto: "Extra-Oral",
  XRay: "X-Ray",
  OpdCard: "OPD Card",
  Document: "เอกสาร",
  Template: "แบบฟอร์มคลินิก",
};

// Categories that offer "take a photo" on phones and tablets.
export const CAMERA_CATEGORIES = [
  "IntraOralPhoto",
  "ExtraOralPhoto",
  "OpdCard",
  "Document",
];

const kindsFor = category => CATEGORY_KINDS[category] || [];

const extensionOf = name => {
  const dot = (name || "").lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
};

/**
 * The kind of a file (jpg, png, pdf, dicom) or null. Browsers send .dcm files
 * with no type, so the extension decides when the type says nothing.
 */
export function kindOf(file) {
  const type = (file && file.type ? file.type : "").toLowerCase();
  const ext = extensionOf(file && file.name);
  const entries = Object.entries(KINDS);
  const byType = type && entries.find(([, k]) => k.mimes.includes(type));
  if (byType) return byType[0];
  const byExt = ext && entries.find(([, k]) => k.exts.includes(ext));
  return byExt ? byExt[0] : null;
}

/** The content type to sign and upload with; falls back to the file's own. */
export function contentTypeOf(file) {
  const kind = kindOf(file);
  return kind ? KINDS[kind].mime : (file && file.type) || "";
}

/** For <input accept>: extensions and types, so .dcm is pickable too. */
export function acceptAttr(category) {
  return kindsFor(category)
    .flatMap(kind => [
      ...KINDS[kind].exts.map(ext => `.${ext}`),
      ...KINDS[kind].mimes,
    ])
    .join(",");
}

/** "JPG · PNG · DICOM" */
export function acceptLabel(category) {
  return kindsFor(category)
    .map(kind => KINDS[kind].label)
    .join(" · ");
}

/** "สูงสุด 25 MB ต่อไฟล์", or per kind when the limits differ. */
export function sizeLabel(category) {
  const kinds = kindsFor(category);
  const limits = [...new Set(kinds.map(kind => KINDS[kind].maxBytes))];
  if (limits.length <= 1) {
    return limits.length ? `สูงสุด ${limits[0] / MB} MB ต่อไฟล์` : "";
  }
  const dicom = kinds.includes("dicom") ? KINDS.dicom.maxBytes / MB : null;
  const other = kinds.find(kind => kind !== "dicom");
  return `รูป ${KINDS[other].maxBytes / MB} MB, DICOM ${dicom} MB ต่อไฟล์`;
}

/**
 * Splits files into those the category takes and those it does not, with the
 * reason shown to the user for each rejected one.
 *
 * @param {File[]} files
 * @param {string} category - a CATEGORY_KINDS key
 * @returns {{ accepted: File[], rejected: Array<{ file: File, name: string, reason: string }> }}
 */
export function checkFiles(files, category) {
  const allowed = kindsFor(category);
  const label = CATEGORY_LABELS[category] || category;
  const typeList = allowed.map(kind => KINDS[kind].label).join(", ");
  const accepted = [];
  const rejected = [];
  (files || []).forEach(file => {
    const kind = kindOf(file);
    const name = (file && file.name) || "ไม่มีชื่อ";
    if (!kind || !allowed.includes(kind)) {
      rejected.push({ file, name, reason: `${label} รับ ${typeList} เท่านั้น` });
      return;
    }
    if (!file.size) {
      rejected.push({ file, name, reason: "ไฟล์ว่าง" });
      return;
    }
    const max = KINDS[kind].maxBytes;
    if (file.size > max) {
      const suffix = kind === "dicom" ? " (ไฟล์ DICOM)" : "";
      rejected.push({ file, name, reason: `ใหญ่เกิน ${max / MB} MB${suffix}` });
      return;
    }
    accepted.push(file);
  });
  return { accepted, rejected };
}

/** Clinic forms must be PDFs: an image is wrapped in a one-page PDF first. */
export function needsPdfWrap(file, category) {
  if (category !== "Template") return false;
  const kind = kindOf(file);
  return kind === "jpg" || kind === "png";
}

export function isImageKind(file) {
  const kind = kindOf(file);
  return kind === "jpg" || kind === "png";
}
